// src/pages/ManagePosts.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useStore } from '../store';
import { getPlatform } from '../utils/platforms';
import { format } from 'date-fns';

export default function ManagePosts() {
  const navigate = useNavigate();
  const { posts, postsTotal, postsLoading, fetchPosts, deletePost } = useStore();
  const [activeTab, setActiveTab] = useState('queued');
  const [search, setSearch] = useState('');

  useEffect(() => { fetchPosts({ status: activeTab === 'pending' ? 'pending_review' : activeTab }); }, [activeTab]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    await deletePost(id);
    toast.success('Post deleted');
  };

  const tabs = [
    { id: 'queued', label: 'Queued Posts' },
    { id: 'draft', label: 'Unscheduled' },
    { id: 'error', label: 'Error' },
    { id: 'delivered', label: 'Delivered' },
    { id: 'pending', label: 'Pending Review' },
  ];

  const filtered = posts.filter(p => !search || p.caption?.toLowerCase().includes(search.toLowerCase()));

  // Group by date
  const grouped = {};
  filtered.forEach(p => {
    const key = p.scheduled_at ? format(new Date(p.scheduled_at), 'EEEE, MMM d, yyyy') : 'Unscheduled';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(p);
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="page-header">
        <span className="page-title">Posts</span>
        <button className="btn btn-primary" onClick={() => navigate('/posts/create')}>+ Create Post</button>
      </div>
      <div style={{ display: 'flex', padding: '0 16px', background: '#fff', borderBottom: '1px solid #e8eaf0', flexShrink: 0 }}>
        {tabs.map(t => <div key={t.id} style={{ padding: '11px 14px', fontSize: 12, cursor: 'pointer', color: activeTab === t.id ? '#f4a118' : '#8892a4', borderBottom: `2px solid ${activeTab === t.id ? '#f4a118' : 'transparent'}`, fontWeight: activeTab === t.id ? 600 : 500, whiteSpace: 'nowrap' }} onClick={() => setActiveTab(t.id)}>{t.label}</div>)}
      </div>
      <div className="toolbar">
        <div className="search-box"><span style={{ color: '#aaa' }}>🔍</span><input placeholder="Search a Post" value={search} onChange={e => setSearch(e.target.value)} /></div>
        <button className="filter-pill">📅 Select Dates</button>
        <button className="filter-pill">⚡ Filter Posts</button>
        <span className="count-text ml-auto">1 — {filtered.length} of {postsTotal}</span>
        <div className="vm-group">
          <div className="vm active">List</div>
          <div className="vm" onClick={() => navigate('/posts/calendar')}>Day</div>
          <div className="vm" onClick={() => navigate('/posts/calendar')}>Week</div>
          <div className="vm" onClick={() => navigate('/posts/calendar')}>Month</div>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
        {postsLoading ? <div className="loading-center"><div className="spinner" /></div> :
          filtered.length === 0 ? <div className="empty-state"><span style={{ fontSize: 40 }}>📭</span><h3>No posts found</h3><p>Create your first post to get started</p><button className="btn btn-primary" onClick={() => navigate('/posts/create')}>Create Post</button></div> :
          Object.entries(grouped).map(([date, datePosts]) => (
            <div key={date}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#8892a4', letterSpacing: '.08em', textTransform: 'uppercase', padding: '8px 0 5px' }}>{date}</div>
              {datePosts.map(p => {
                const accData = p.accounts?.filter(a => a && a.platform);
                return (
                  <div key={p.id} style={{ background: '#fff', border: '1px solid #e8eaf0', borderRadius: 8, display: 'flex', marginBottom: 8, overflow: 'hidden' }}>
                    <div style={{ width: 72, minHeight: 72, background: '#eef2f7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 22, color: '#c0c7d8' }}>🖼</div>
                    <div style={{ flex: 1, padding: '9px 12px', minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#1a1f2e', marginBottom: 3 }}>Post</div>
                      <div style={{ fontSize: 11, color: '#5a6578', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.caption}</div>
                      {p.tags?.length > 0 && <div style={{ fontSize: 10, color: '#0f766e', marginTop: 2 }}>{p.tags.join(' ')}</div>}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#0f766e', marginTop: 4 }}>
                        📅 {p.scheduled_at ? format(new Date(p.scheduled_at), 'MMM d, yyyy h:mm a') : 'Not scheduled'}
                      </div>
                    </div>
                    <div style={{ padding: '9px 12px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, minWidth: 160, borderLeft: '1px solid #f0f1f5' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'flex-end' }}>
                        {accData?.slice(0, 3).map((a, i) => { const pl = getPlatform(a.platform); return <div key={i} className="platform-icon" style={{ background: pl.bg, color: pl.color, width: 18, height: 18 }}>{pl.icon}</div>; })}
                      </div>
                      <div style={{ fontSize: 10, color: '#aaa' }}>{p.created_by_name || 'Former member'} · {format(new Date(p.created_at || Date.now()), 'MMM d, yyyy')}</div>
                      <span className={`badge badge-${p.status}`}>{p.status}</span>
                        {/* Per-account error details */}
                      {p.accounts?.some(a => a?.status === 'error') && (
                        <div style={{ marginTop: 4, width: '100%' }}>
                          {p.accounts.filter(a => a?.status === 'error').map((a, i) => (
                            <div key={i} style={{ fontSize: 9, color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4, padding: '2px 6px', marginBottom: 2, lineHeight: 1.4 }}>
                              ❌ {a.platform}: {a.error_message || 'Failed'}
                            </div>
                          ))}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 5, marginTop: 3 }}>
                        <button style={{ padding: '3px 8px', border: '1px solid #e2e5ed', borderRadius: 4, fontSize: 10, cursor: 'pointer', background: '#fff' }} onClick={() => navigate(`/posts/edit/${p.id}`)}>✏ Edit</button>
                        <button style={{ padding: '3px 8px', border: '1px solid #fecaca', borderRadius: 4, fontSize: 10, cursor: 'pointer', background: '#fff', color: '#e55b2d' }} onClick={() => handleDelete(p.id)}>🗑</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        }
      </div>
    </div>
  );
}
